<template>
  <section id="content" class="show-impressum flex justify-center relative">

    <div id="map" class="w-full h-full"></div>

    <TouchSearchBar class="fixed !w-[600px] top-44"/>

    <InfoCard
        :is-open="showCareerDetail"
        :company="selectedMarker"
        :show-about-us="showAboutUs"
        :show-offer="showOffer"
        @close="toggleShowDetail"
    />

  </section>
</template>

<script setup>
import { ref, reactive, onMounted, provide } from 'vue';
import CompanyDetail from "@/components/ui/CompanyDetail.vue";
import EventBus from "@/EventBus";
import database from "@/database.json";
import { performRequest } from "@/API/Services/APIService";
import APIEndpoints from "@/API/Services/APIEndpoints";
import SearchElement from "@/components/ui/SearchElement.vue";
import {next} from "lodash/seq";
import {getCoordinates} from "@/utils/getDisplayAddress";

// Components
const TouchSearchBar = SearchElement;
const InfoCard = CompanyDetail;

// Refs & reactive state
const map = ref(null);
const currentLocation = reactive({ lat: 48.7758, lng: 9.1829 });
const markers = ref([]);
const activeMarker = ref(null);
const showCareerDetail = ref(false);
const selectedMarker = ref(null);
const showOffer = ref(true);
const showAboutUs = ref(true);
const databaseData = ref(database);

// Map & circle options
const mapOptions = reactive({
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: false,
  styles: [
    {
      "featureType": "all",
      "elementType": "geometry",
      "stylers": [
        { "color": "#77B9BA" }
      ]
    },
    {
      "featureType": "all",
      "elementType": "labels.text.fill",
      "stylers": [
        { "color": "#212121" }
      ]
    },
    {
      "featureType": "all",
      "elementType": "labels.text.stroke",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "landscape",
      "elementType": "geometry.fill",
      "stylers": [
        { "color": "#88c0c0" }
      ]
    },
    {
      "featureType": "landscape.man_made",
      "elementType": "geometry",
      "stylers": [
        { "color": "#77B9BA" }
      ]
    },
    {
      "featureType": "poi",
      "elementType": "geometry",
      "stylers": [
        { "color": "#7fc0bf" }
      ]
    },
    {
      "featureType": "poi.park",
      "elementType": "geometry",
      "stylers": [
        { "color": "#6eb0b0" }            ]
    },
    {
      "featureType": "poi.business",
      "elementType": "labels.text.fill",
      "stylers": [
        { "color": "#6c6c6c" }
      ]
    },
    {
      "featureType": "road",
      "elementType": "geometry",
      "stylers": [
        { "color": "#82c4c4" }
      ]
    },
    {
      "featureType": "road.arterial",
      "elementType": "geometry",
      "stylers": [
        { "color": "#6eb0b0" }
      ]
    },
    {
      "featureType": "road.highway",
      "elementType": "geometry",
      "stylers": [
        { "color": "#E1A325" }
      ]
    },
    {
      "featureType": "road.highway",
      "elementType": "geometry.stroke",
      "stylers": [
        { "color": "rgba(48,56,65,0.3)" }
      ]
    },
    {
      "featureType": "road.local",
      "elementType": "geometry",
      "stylers": [
        { "color": "#82c4c4" }
      ]
    },
    {
      "featureType": "administrative",
      "elementType": "geometry.stroke",
      "stylers": [
        { "color": "rgba(48,56,65,0.3)" }
      ]
    },
    {
      "featureType": "water",
      "elementType": "geometry",
      "stylers": [
        { "color": "#dff8f9" }
      ]
    },
    {
      "featureType": "water",
      "elementType": "labels.text.fill",
      "stylers": [
        { "color": "#212121" }
      ]
    },
    {
      "featureType": "administrative.land_parcel",
      "elementType": "labels",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "poi",
      "elementType": "labels",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "poi.business",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "road",
      "elementType": "labels.icon",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "road.local",
      "elementType": "labels",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "road.highway",
      "elementType": "labels",
      "stylers": [
        { "visibility": "off" }
      ]
    },
    {
      "featureType": "transit",
      "stylers": [
        { "visibility": "off" }
      ]
    }
  ],
  restriction: {
    latLngBounds: {
      north: 49.8845,
      south: 47.5,
      west: 7.464722,
      east: 10.491524
    }
  }
});

// Funktionen
const getCurrentPosition = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
        position => {
          currentLocation.lat = position.coords.latitude;
          currentLocation.lng = position.coords.longitude;
        },
        error => console.error('Fehler bei der Geolokalisierung:', error)
    );
  } else {
    console.error('Geolokalisierung wird nicht unterstützt');
  }
};

const initMap = () => {
  map.value = new window.google.maps.Map(document.getElementById('map'), {
    center: currentLocation,
    zoom: 12,
    options: mapOptions,
    minZoom: 10,
    maxZoom: 20
  });
};

const initMarkers = async () => {

  try {

    const validMarkers = markers.value.filter(m => getCoordinates(m)) // ohne Koordinaten kein Marker

    markers.value = validMarkers.map(m => {
      const coordinates = getCoordinates(m)
      const marker = new window.google.maps.Marker({
        position: { lat: coordinates[1], lng: coordinates[0] },
        map: map.value,
        clickable: true,
        title: m.company.companycaption1,
      });

      marker.companyid = m.company.companyid;

      marker.activateBounce = () => marker.setAnimation(window.google.maps.Animation.BOUNCE);
      marker.deactivateBounce = () => marker.setAnimation(null);

      marker.addListener('click', () => onMarkerClick(m));

      return marker;
    });

  } catch (e) {
    console.error(e);
  }
};

const onMarkerClick = (company) => {
  selectedMarker.value = company;
  setTimeout(() => {
    showCareerDetail.value = true;
  }, 100);
};

const navigateMapTo = (company, zoom = 14) => {

  const targetCoordinates = getCoordinates(company);
  if (!targetCoordinates) return;

  const newCenter = new window.google.maps.LatLng(targetCoordinates[1], targetCoordinates[0]);

  map.value.panTo(newCenter);
  map.value.setZoom(zoom);

  const marker = markers.value.find(m => m.companyid === company.company.companyid);
  if (!marker) return;

  if (activeMarker.value) {
    activeMarker.value.deactivateBounce();
    activeMarker.value.setZIndex(1);
  }

  activeMarker.value = marker;
  activeMarker.value.activateBounce();
};

const toggleShowDetail = () => {
  showCareerDetail.value = !showCareerDetail.value;
  if(!showCareerDetail.value) {
    if (activeMarker.value) activeMarker.value.deactivateBounce()
  }
};

// Lifecycle
onMounted(async () => {
  try {
    const data = await performRequest(APIEndpoints.GET_COMPANIES, { query: "?limit=1000&format=json&range=0" });
    markers.value = data.companies;
    console.log(markers.value)
  } catch (err) {
    console.error("error: ",err);
    markers.value = databaseData.value;
  }

  document.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', event => event.preventDefault());
  });

  getCurrentPosition();
  initMap();
  await initMarkers();

  EventBus.on("inputEvent", () => {
    if(activeMarker.value) activeMarker.value.deactivateBounce()
  })

});

// Provide für Kinderkomponenten
provide('onMarkerClick', onMarkerClick);
provide('navigateMapTo', navigateMapTo);

</script>

<style scoped>
@import "@/styles/textStyle.css";
@import "@/styles/generalStyle.css";

#content {
  position: relative;
  transition: height 1s ease-in-out;
}

#map {
  width: 100%;
  height: 100%;
}

a {
  display: none;
}
</style>
